import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, theme } from 'antd';
import { CloseOutlined, CameraOutlined, BulbOutlined, HistoryOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import SeverityPicker from './SeverityPicker';
import { severidadColor } from './maintenanceColors';
import HallazgoFotosGallery from 'components/hallazgos/HallazgoFotosGallery';
import HallazgoFotosUpload from 'components/hallazgos/HallazgoFotosUpload';
import ComponentTreeSelect from 'pages/liftlogic/v2/Components/ComponentTreeSelect';
import { useRehydratePendingPhotos } from 'hooks/useRehydratePendingPhotos';

// Fila compacta de UN hallazgo (incidencia o sugerencia) dentro de la lista de
// un ítem del checklist. Variante B (densa): descripción inline editable +
// severidad (incidencia) + fotos por `tempId` + quitar. Las fotos se suben al
// idHallazgo real cuando aparece tras el PUT (resuelto por tempId).
function HallazgoRow({
  tipo,
  hallazgo,
  idHallazgoResuelto,
  onUpdate,
  onRemove,
  mantencionId,
  isPro,
  equipoId,
  saveTick,
  showFotos = true,
  showSeveridad = true,
  autoFocus = false,
  // Si esta fila está vacía, ¿su falta de descripción es la que realmente
  // bloquea el cierre? El gate real es por-ítem (basta con que UNA incidencia
  // tenga texto), así que una fila vacía no bloquea si otra hermana ya tiene
  // texto. Default true: preserva el comportamiento esperado cuando el
  // caller no tiene contexto de las hermanas (ej. renders standalone).
  descripcionBloquea = true,
  // ¿El tenant exige severidad en toda incidencia con texto? (mantCfg.severidad
  // && mantCfg.severidadObligatoria, calculado por el caller). Default false:
  // no bloquea por severidad salvo que el caller lo indique explícitamente.
  exigeSeveridad = false,
}) {
  const { token } = theme.useToken();
  const isInc = tipo === 'INCIDENCIA';
  const tempId = hallazgo.tempId;
  // idHallazgo efectivo para fotos: el backfill en obsSug o el map tempId→id.
  const idHallazgo = hallazgo.idHallazgo ?? idHallazgoResuelto ?? null;

  // La descripción de una incidencia es obligatoria si es la única vía para
  // que el ítem tenga al menos una incidencia con texto (ver
  // `descripcionBloquea`). La severidad es obligatoria si el tenant lo exige
  // y la incidencia ya tiene texto. Ambos casos pintan la fila como error;
  // son excluyentes entre sí (si falta la descripción, ese es el problema a
  // mostrar) y ambos reflejan exactamente lo que bloquea el cierre real
  // (`isItemIncompleto` en MaintanceToDoItemList / el gate del provider).
  const descripcionVacia = isInc && String(hallazgo.descripcion || '').trim() === '';
  const faltaDescripcion = descripcionVacia && descripcionBloquea;
  // Invariante implícito: exigeSeveridad implica showSeveridad (el caller en
  // MaintanceToDoItemList garantiza que exigeSeveridad solo es true cuando
  // showSeveridad también lo es). Sin este invariante, la fila quedaría neutra
  // aunque la severidad fuese requerida.
  const faltaSeveridad =
    isInc && !descripcionVacia && exigeSeveridad && showSeveridad && !hallazgo.severidad;
  const bloquea = faltaDescripcion || faltaSeveridad;
  const descId = `hallazgo-desc-${tempId}`;

  const [pending, setPending] = useState([]);
  const [fotoCount, setFotoCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const uploadRef = useRef(null);
  const descRef = useRef(null);

  useRehydratePendingPhotos({ mantencionId, tempId, setPending });

  // Revocar blob URLs al desmontar la fila (modo controlado: el dueño revoca).
  const pendingRef = useRef(pending);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);
  useEffect(() => {
    return () => pendingRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
  }, []);

  // Auto-subida: cuando aparece el idHallazgo (tras el PUT) y hay fotos
  // pendientes acumuladas, subirlas al hallazgo real.
  useEffect(() => {
    if (!idHallazgo) return;
    const ref = uploadRef.current;
    if (ref?.hasPending()) {
      ref.uploadPending(idHallazgo).then(() => setRefreshKey((k) => k + 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHallazgo]);

  // Al sembrarse la incidencia (el técnico marcó el ítem "Con problema"), traer
  // el campo a la vista y enfocarlo: en el celular abre el teclado, que es la
  // señal más clara de que acá hay que escribir. useLayoutEffect (no
  // useEffect): el focus() corre síncrono dentro del mismo commit del click,
  // preservando la activación del gesto del usuario — iOS Safari solo levanta
  // el teclado si el focus() ocurre dentro de ese task, no en un efecto pasivo
  // posterior al paint. `block: 'nearest'` evita re-centrar la pantalla en
  // marcados sucesivos (con 'center' cada ítem marcado corría el siguiente de
  // debajo del dedo).
  useLayoutEffect(() => {
    if (!autoFocus) return;
    const node = descRef.current;
    if (!node) return;
    node.focus();
    node.resizableTextArea?.textArea?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const totalFotos = fotoCount + pending.length;

  return (
    <div
      style={{
        border: `1px solid ${
          bloquea ? token.colorError : isInc ? token.colorErrorBorder : token.colorBorderSecondary
        }`,
        borderRadius: token.borderRadius,
        background: bloquea ? token.colorErrorBg : token.colorBgContainer,
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {/* Header compacto: chip severidad / 💡 + contador de fotos + quitar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isInc ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 999,
              padding: '1px 8px',
              fontSize: 11,
              fontWeight: 600,
              background: hallazgo.severidad
                ? `${severidadColor(hallazgo.severidad)}22`
                : token.colorFillTertiary,
              color: hallazgo.severidad
                ? severidadColor(hallazgo.severidad)
                : token.colorTextTertiary,
            }}
          >
            {hallazgo.severidad || 'Sin severidad'}
          </span>
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: token.colorWarning,
            }}
          >
            <BulbOutlined /> Sugerencia
          </span>
        )}
        {totalFotos > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 11,
              color: token.colorTextTertiary,
            }}
          >
            <CameraOutlined /> {totalFotos}
          </span>
        )}
        {hallazgo.arrastradoDe?.firstSeenAt && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 999,
              padding: '1px 8px',
              fontSize: 11,
              fontWeight: 500,
              background: token.colorInfoBg,
              color: token.colorInfoText,
            }}
          >
            <HistoryOutlined />
            Viene de la visita del{' '}
            {new Date(hallazgo.arrastradoDe.firstSeenAt).toLocaleDateString('es-CL')}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined style={{ fontSize: 11 }} />}
          onClick={onRemove}
          title={isInc ? 'Quitar incidencia' : 'Quitar sugerencia'}
          style={{ color: token.colorTextTertiary }}
        />
      </div>

      {/* Descripción: etiqueta explícita + estado de error mientras falte. El
          placeholder solo da un ejemplo; la obligatoriedad la comunica la etiqueta. */}
      <div>
        <label
          htmlFor={descId}
          style={{
            display: 'block',
            marginBottom: 4,
            fontSize: 12,
            fontWeight: 600,
            color: faltaDescripcion ? token.colorError : token.colorText,
          }}
        >
          {isInc ? 'Describe el problema' : 'Recomendación'}{' '}
          {isInc ? (
            <span style={{ color: token.colorError }}>*</span>
          ) : (
            <span style={{ fontWeight: 400, color: token.colorTextTertiary }}>(opcional)</span>
          )}
        </label>
        <TextArea
          id={descId}
          ref={descRef}
          value={hallazgo.descripcion || ''}
          onChange={(e) => onUpdate({ descripcion: e.target.value })}
          autoSize={{ minRows: 3, maxRows: 6 }}
          status={faltaDescripcion ? 'error' : undefined}
          aria-invalid={faltaDescripcion || undefined}
          placeholder={
            isInc
              ? 'Ej.: la puerta del piso 3 no cierra completa, roza en la guía'
              : 'Ej.: conviene cambiar las gomas de las puertas en la próxima visita'
          }
        />
        {faltaDescripcion && (
          <p style={{ margin: '4px 0 0', fontSize: 11.5, color: token.colorError }}>
            Sin esta descripción no se puede finalizar la mantención.
          </p>
        )}
      </div>

      {/* Severidad (solo incidencia) — aviso bloqueante si el tenant la exige
          y falta, en paridad con el aviso de descripción de arriba. */}
      {isInc && showSeveridad && (
        <div>
          <SeverityPicker value={hallazgo.severidad} onPick={(v) => onUpdate({ severidad: v })} />
          {faltaSeveridad && (
            <p style={{ margin: '4px 0 0', fontSize: 11.5, color: token.colorError }}>
              Falta la criticidad para poder finalizar la mantención.
            </p>
          )}
        </div>
      )}

      {/* Componente afectado (pro, opcional) */}
      {isPro && (
        <ComponentTreeSelect
          equipoId={equipoId}
          value={hallazgo.idComponente ?? undefined}
          onChange={(v) => onUpdate({ idComponente: v ?? null })}
          placeholder="Componente afectado (opcional)"
        />
      )}

      {/* Fotos por hallazgo (keyeadas por tempId) */}
      {showFotos && (
        <div>
          {idHallazgo && (
            <HallazgoFotosGallery
              idHallazgo={idHallazgo}
              refreshKey={refreshKey}
              editable
              onCount={setFotoCount}
            />
          )}
          <div style={{ marginTop: idHallazgo ? 8 : 0 }}>
            <HallazgoFotosUpload
              ref={uploadRef}
              idHallazgo={idHallazgo}
              currentCount={fotoCount}
              onUploaded={() => setRefreshKey((k) => k + 1)}
              pendingFiles={pending}
              onPendingFilesChange={setPending}
              mantencionId={mantencionId}
              tempId={tempId}
            />
          </div>
          {!idHallazgo && pending.length > 0 && (
            <p
              style={{
                marginTop: 6,
                marginBottom: 0,
                fontSize: 11,
                color: token.colorTextTertiary,
                fontStyle: 'italic',
              }}
            >
              La foto se guardará junto al hallazgo cuando agregues una descripción y guardes.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default HallazgoRow;
